import { zodResolver } from '@hookform/resolvers/zod';
import type { AccountInput, NewAccount } from '@vy/types';
import { NewAccountSchema } from '@vy/types';
import { isNotNullOrUndefined, validateNotEmptyString } from '@vy/utils';
import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAccount } from '../../../../../../hooks/requests/use-account';
import { useUpdateAccount } from '../../../../../../hooks/requests/use-update-account';
import { useUpdateAccountLogo } from '../../../../../../hooks/requests/use-update-account-logo';
import { queryKeys } from '../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../utils/vy-mutation';

export const AccountForm: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();
  const accountQuery = useAccount();
  const updateAccountMutation = useUpdateAccount();
  const updateAccountLogoMutation = useUpdateAccountLogo();

  const methods = useForm<AccountInput, unknown, NewAccount>({
    resolver: zodResolver(NewAccountSchema),
    defaultValues: accountQuery?.data?.account,
  });

  useEffect(() => {
    if (accountQuery.data?.account) {
      methods.reset(accountQuery.data.account);
    }
  }, [accountQuery.data]);

  const isLogoChanged = (logo?: string) => isNotNullOrUndefined(logo) && logo.startsWith('data:image');

  const updateLogo = async (logo?: string) => {
    validateNotEmptyString(logo, 'Logo');

    await runMutation(
      updateAccountLogoMutation,
      { logo },
      {
        pending: 'Updating logo...',
        success: 'Logo updated',
        error: 'Failed to update logo',
      },
    );
  };

  const handleSubmit = methods.handleSubmit(async (newAccount) => {
    const { logoUrl, ...accountData } = newAccount;

    if (isLogoChanged(logoUrl)) {
      await updateLogo(logoUrl);
    }

    await runMutation(
      updateAccountMutation,
      { account: accountData },
      {
        pending: 'Updating account...',
        success: 'Account updated',
        error: 'Failed to update account',
      },
    );
    await queryClient.refetchQueries(queryKeys.account());
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} className="h-full w-full">
        {children}
      </form>
    </FormProvider>
  );
};

export const withAccountForm = <ComponentProps extends Record<string, unknown>>(
  Component: React.ComponentType<ComponentProps>,
) => {
  return (componentProps: ComponentProps) => {
    return (
      <AccountForm>
        <Component {...componentProps} />
      </AccountForm>
    );
  };
};
